import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferOutput } from "@trpc/tanstack-react-query";
import { queryClient, trpc } from "~/utils/trpc";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { DataTable } from "@quake/shared-ui/components/dataTable/dataTable";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Textarea } from "@quake/shared-ui/components/ui/textarea";
import { Checkbox } from "@quake/shared-ui/components/ui/checkbox";
import { Label } from "@radix-ui/react-label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { Badge } from "@quake/shared-ui/components/ui/badge";
import { HumanDateWithTooltip } from "@quake/shared-ui/components/ui/human-date-with-tooltip";
import { toast } from "@quake/shared-ui/lib/sonner";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import Trash2Icon from "lucide-react/dist/esm/icons/trash-2";
import RefreshCcwIcon from "lucide-react/dist/esm/icons/refresh-ccw";
import { hasPermission, useHasPermission } from "~/store/usePermissionStore";
import {
  buildInstalledAppPermissionResource,
  buildInstalledAppScope,
  getOAuthScopeDefinition,
  parseInstalledAppScope,
  togglePermissionEntries,
} from "@quake/isomorphic/authorization";

type OAuthClient = inferOutput<typeof trpc.oauthClients.list>[number];

type ClientFormState = {
  name: string;
  description: string;
  scopes: string[];
  redirect_uris: string[];
  is_active: boolean;
};

const emptyFormState: ClientFormState = {
  name: "",
  description: "",
  scopes: [],
  redirect_uris: [],
  is_active: true,
};

const APP_SCOPE_PREVIEW_LIMIT = 8;

function getScopeDisplayLabel(scope: string, scopeLabels: Record<string, string>) {
  const installedAppScope = parseInstalledAppScope(scope);
  if (installedAppScope) {
    const appName = scopeLabels[buildInstalledAppScope({ ...installedAppScope, actionId: "*" })]?.replace(
      /^Allow all actions for /,
      "",
    );
    if (!appName) return scope;
    const actionLabel = scopeLabels[scope];
    const appSuffix = ` for ${appName}`;
    const actionName =
      installedAppScope.actionId === "*"
        ? "*"
        : actionLabel?.startsWith("Allow ") && actionLabel.endsWith(appSuffix)
          ? actionLabel.slice("Allow ".length, -appSuffix.length)
          : installedAppScope.actionId;
    return `${appName}:${actionName}`;
  }

  const customEntityScope = scope.match(/^customEntities\.[^:]+:(read|write)$/);
  if (customEntityScope) {
    const action = customEntityScope[1];
    const labelPrefix = `Allow ${action} access to custom entity `;
    const label = scopeLabels[scope];
    if (label?.startsWith(labelPrefix)) return `${label.slice(labelPrefix.length)}:${action}`;
  }

  return scope;
}

export const Route = createFileRoute("/_dashboard/$datasetSlug/settings/api")({
  beforeLoad: async ({ params }) => {
    const session = await queryClient.fetchQuery(trpc.auth.getSession.queryOptions()).catch(() => null);

    if (
      !hasPermission({ role: session?.user?.role, permissions: session?.user?.permissions }, "oauthClients", "view")
    ) {
      throw redirect({ to: "/$datasetSlug/dashboard", params: { datasetSlug: params.datasetSlug } });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const qc = useQueryClient();
  const { datasetSlug } = Route.useParams();
  useBreadCrumbs([{ title: "API", link: `/${datasetSlug}/settings/api` }]);
  const canCreateOauthClients = useHasPermission("oauthClients", "create");
  const canUpdateOauthClients = useHasPermission("oauthClients", "update");
  const canDeleteOauthClients = useHasPermission("oauthClients", "delete");

  const [formState, setFormState] = useState<ClientFormState>(emptyFormState);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<OAuthClient | null>(null);
  const [selectedClient, setSelectedClient] = useState<OAuthClient | null>(null);
  const [generatedSecret, setGeneratedSecret] = useState<{ client_id: string; client_secret: string } | null>(null);
  const [redirectUriDraft, setRedirectUriDraft] = useState("");
  const [scopeSearch, setScopeSearch] = useState("");
  const [expandedScopeGroups, setExpandedScopeGroups] = useState<Set<string>>(() => new Set());

  const { data: clients, status } = useQuery(trpc.oauthClients.list.queryOptions());
  const { data: scopeLabels = {} } = useQuery(trpc.oauthClients.getAvailableScopesMap.queryOptions());
  const availableScopeOptions = useMemo(() => Object.keys(scopeLabels).sort(), [scopeLabels]);
  const scopeOptions = useMemo(
    () => Array.from(new Set([...availableScopeOptions, ...formState.scopes])).sort(),
    [availableScopeOptions, formState.scopes],
  );
  const normalizedScopeSearch = scopeSearch.trim().toLowerCase();
  const filteredScopes = scopeOptions.filter(
    (scope) =>
      scope.toLowerCase().includes(normalizedScopeSearch) ||
      (scopeLabels[scope] ?? "").toLowerCase().includes(normalizedScopeSearch),
  );
  const scopeGroups = useMemo(() => {
    const groups = new Map<string, { title: string; scopes: string[] }>();
    for (const scope of filteredScopes) {
      const installedAppScope = parseInstalledAppScope(scope);
      const key = installedAppScope ? `app-${installedAppScope.installedAppId}` : "general";
      const wildcardLabel = installedAppScope ? scopeLabels[`app-${installedAppScope.installedAppId}:*`] : undefined;
      const title = installedAppScope
        ? wildcardLabel?.replace(/^Allow all actions for /, "") || installedAppScope.installedAppId
        : "General API scopes";
      const group = groups.get(key) ?? { title, scopes: [] };
      group.scopes.push(scope);
      groups.set(key, group);
    }
    return Array.from(groups.entries()).map(([id, group]) => ({ id, ...group }));
  }, [filteredScopes, scopeLabels]);

  const createClient = useMutation(
    trpc.oauthClients.create.mutationOptions({
      onSuccess: (res) => {
        qc.invalidateQueries(trpc.oauthClients.list.queryFilter());
        setFormOpen(false);
        setFormState(emptyFormState);
        setGeneratedSecret({ client_id: res.client.client_id, client_secret: res.client_secret });
        setSecretDialogOpen(true);
        toast.success("API credential created");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const updateClient = useMutation(
    trpc.oauthClients.update.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries(trpc.oauthClients.list.queryFilter());
        setFormOpen(false);
        setEditingClient(null);
        toast.success("API credential updated");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const deleteClient = useMutation(
    trpc.oauthClients.delete.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries(trpc.oauthClients.list.queryFilter());
        setDeleteDialogOpen(false);
        setSelectedClient(null);
        toast.success("API credential deleted");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const regenerateSecret = useMutation(
    trpc.oauthClients.regenerateSecret.mutationOptions({
      onSuccess: (res) => {
        qc.invalidateQueries(trpc.oauthClients.list.queryFilter());
        setRegenerateDialogOpen(false);
        setSelectedClient(null);
        setGeneratedSecret({ client_id: res.client_id, client_secret: res.client_secret });
        setSecretDialogOpen(true);
        toast.success("Client secret regenerated");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const openCreateDialog = () => {
    if (!canCreateOauthClients) return;
    setEditingClient(null);
    setFormState(emptyFormState);
    setRedirectUriDraft("");
    setScopeSearch("");
    setExpandedScopeGroups(new Set());
    setFormOpen(true);
  };

  const openEditDialog = (client: OAuthClient) => {
    if (!canUpdateOauthClients) return;
    setEditingClient(client);
    setRedirectUriDraft("");
    setScopeSearch("");
    setExpandedScopeGroups(new Set());
    setFormState({
      name: client.name,
      description: client.description || "",
      scopes: client.scopes || [],
      redirect_uris: client.redirect_uris || [],
      is_active: !!client.is_active,
    });
    setFormOpen(true);
  };

  const handleSubmit = () => {
    if (editingClient && !canUpdateOauthClients) {
      toast.error("You don't have permission to update API clients");
      return;
    }
    if (!editingClient && !canCreateOauthClients) {
      toast.error("You don't have permission to create API clients");
      return;
    }
    if (!formState.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (formState.scopes.length === 0) {
      toast.error("Select at least one scope");
      return;
    }

    const payload = {
      name: formState.name.trim(),
      description: formState.description.trim() || null,
      scopes: formState.scopes,
      redirect_uris: Array.from(
        new Set(formState.redirect_uris.map((uri) => uri.trim()).filter((uri) => uri.length > 0)),
      ),
      is_active: formState.is_active,
    };

    if (editingClient) {
      updateClient.mutate({ id: editingClient.id, data: payload });
      return;
    }

    createClient.mutate(payload);
  };

  const toggleScope = (scope: string, checked: boolean) => {
    const installedAppScope = parseInstalledAppScope(scope);
    if (!checked && installedAppScope?.actionId === "*") {
      toast.info(
        "This restricts action execution for the app. The client's integration-management scope is unchanged.",
      );
    }
    const wildcardScope = installedAppScope
      ? buildInstalledAppScope({ installedAppId: installedAppScope.installedAppId, actionId: "*" })
      : null;
    if (
      !checked &&
      installedAppScope &&
      installedAppScope.actionId !== "*" &&
      wildcardScope &&
      formState.scopes.includes(wildcardScope)
    ) {
      toast.info(
        "This replaces All actions with individual scopes. The client's integration-management scope is unchanged.",
      );
    }

    setFormState((prev) => {
      if (installedAppScope) {
        const resource = buildInstalledAppPermissionResource(installedAppScope.installedAppId);
        const appPermissions = prev.scopes.flatMap((currentScope) => {
          const parsedScope = parseInstalledAppScope(currentScope);
          return parsedScope?.installedAppId === installedAppScope.installedAppId
            ? [{ resource, action: parsedScope.actionId, active: true }]
            : [];
        });
        const otherScopes = prev.scopes.filter(
          (currentScope) => parseInstalledAppScope(currentScope)?.installedAppId !== installedAppScope.installedAppId,
        );
        const explicitActions = availableScopeOptions.flatMap((availableScope) => {
          const parsedScope = parseInstalledAppScope(availableScope);
          return parsedScope?.installedAppId === installedAppScope.installedAppId && parsedScope.actionId !== "*"
            ? [parsedScope.actionId]
            : [];
        });
        const updatedPermissions = togglePermissionEntries(
          appPermissions,
          resource,
          installedAppScope.actionId,
          checked,
          explicitActions,
        );
        return {
          ...prev,
          scopes: [
            ...otherScopes,
            ...updatedPermissions.map((permission) =>
              buildInstalledAppScope({
                installedAppId: installedAppScope.installedAppId,
                actionId: permission.action,
              }),
            ),
          ],
        };
      }

      const next = new Set(prev.scopes);
      if (checked) {
        next.add(scope);
      } else {
        next.delete(scope);
      }
      return { ...prev, scopes: Array.from(next) };
    });
  };

  const addRedirectUri = () => {
    const trimmed = redirectUriDraft.trim();
    if (!trimmed) return;
    setFormState((prev) => ({
      ...prev,
      redirect_uris: Array.from(new Set([...prev.redirect_uris, trimmed])),
    }));
    setRedirectUriDraft("");
  };

  const removeRedirectUri = (uri: string) => {
    setFormState((prev) => ({
      ...prev,
      redirect_uris: prev.redirect_uris.filter((value) => value !== uri),
    }));
  };

  const copyValue = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Failed to copy ${label}`);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "API Client",
        accessorKey: "name",
        cell: ({ row }: { row: { original: OAuthClient } }) => (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate font-medium">{row.original.name}</span>
            <button
              type="button"
              className="cursor-pointer truncate text-left font-mono text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Copy client ID for ${row.original.name}`}
              title={`Click to copy: ${row.original.client_id}`}
              onClick={() => copyValue(row.original.client_id, "Client ID")}
            >
              {row.original.client_id}
            </button>
          </div>
        ),
      },
      {
        header: "Scopes",
        cell: ({ row }: { row: { original: OAuthClient } }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.scopes?.length ? (
              row.original.scopes.map((scope) => {
                return (
                  <Badge
                    key={scope}
                    variant="secondary"
                    title={scope}
                    className="max-md:max-w-full max-md:whitespace-normal max-md:break-all"
                  >
                    {getScopeDisplayLabel(scope, scopeLabels)}
                  </Badge>
                );
              })
            ) : (
              <span className="text-xs text-muted-foreground">No scopes</span>
            )}
          </div>
        ),
      },
      {
        header: "Status",
        cell: ({ row }: { row: { original: OAuthClient } }) =>
          row.original.is_active ? <Badge variant="default">Active</Badge> : <Badge variant="outline">Disabled</Badge>,
      },
      {
        header: "Created",
        cell: ({ row }: { row: { original: OAuthClient } }) =>
          row.original.created_at ? <HumanDateWithTooltip date={row.original.created_at} /> : "-",
      },
      ...(canUpdateOauthClients || canDeleteOauthClients
        ? [
            {
              header: "Actions",
              cell: ({ row }: { row: { original: OAuthClient } }) => (
                <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-1">
                  {canUpdateOauthClients ? (
                    <button
                      type="button"
                      aria-label="Edit API client"
                      className="cursor-pointer text-blue-500 max-md:flex max-md:size-11 max-md:items-center max-md:justify-center"
                      onClick={() => openEditDialog(row.original)}
                    >
                      <EditIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                  {canUpdateOauthClients ? (
                    <button
                      type="button"
                      aria-label="Regenerate client secret"
                      className="cursor-pointer text-amber-500 max-md:flex max-md:size-11 max-md:items-center max-md:justify-center"
                      onClick={() => {
                        setSelectedClient(row.original);
                        setRegenerateDialogOpen(true);
                      }}
                    >
                      <RefreshCcwIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                  {canDeleteOauthClients ? (
                    <button
                      type="button"
                      aria-label="Delete API client"
                      className="cursor-pointer text-red-500 max-md:flex max-md:size-11 max-md:items-center max-md:justify-center"
                      onClick={() => {
                        setSelectedClient(row.original);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2Icon className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canDeleteOauthClients, canUpdateOauthClients, clients, scopeLabels],
  );

  return (
    <div className="space-y-6">
      <div className="flex max-md:flex-col max-md:items-stretch max-md:gap-3 md:items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">API Access</h1>
          <p className="text-sm text-muted-foreground">
            Manage API access rights for third party integrations via the{" "}
            <a
              href="https://api.quake.dev/documentation"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500"
            >
              Quake Core OpenAPI
            </a>{" "}
            specification.
          </p>
        </div>
        {canCreateOauthClients ? <Button onClick={openCreateDialog}>New API Client</Button> : null}
      </div>

      <DataTable
        mobileLayout="cards"
        data={clients ?? []}
        isLoading={status === "pending"}
        showPagination={false}
        showSearchFilter={false}
        showColumnFilter
        columns={columns}
      />

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4 max-md:[overflow-wrap:anywhere]">
          <DialogHeader className="max-md:pr-6">
            <DialogTitle>{editingClient ? "Edit API Client" : "Create API Client"}</DialogTitle>
            <DialogDescription>
              Configure the API client settings{" "}
              {editingClient ? (
                <>
                  <span className="text-muted-foreground">for</span>
                  <code className="bg-slate-200 inline-block px-1 max-md:break-all">{editingClient.client_id}</code>
                </>
              ) : (
                "for a new client"
              )}
              .
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formState.name}
                onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Integrations Bot"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formState.description}
                onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
            <div>
              <Label>Scopes</Label>
              <Input
                value={scopeSearch}
                onChange={(e) => setScopeSearch(e.target.value)}
                placeholder="Search scopes"
                className="mt-2"
              />
              <div className="mt-2 max-h-56 space-y-2 overflow-y-auto rounded-md border p-3">
                {scopeOptions.length ? (
                  filteredScopes.length ? (
                    scopeGroups.map((group) => {
                      const appWildcardScopes = group.scopes.filter(
                        (scope) => parseInstalledAppScope(scope)?.actionId === "*",
                      );
                      const appActionScopes = group.scopes.filter((scope) => {
                        const parsedScope = parseInstalledAppScope(scope);
                        return parsedScope && parsedScope.actionId !== "*";
                      });
                      const hasManyAppActions = appActionScopes.length > APP_SCOPE_PREVIEW_LIMIT;
                      const isExpanded = expandedScopeGroups.has(group.id);
                      const visibleScopes =
                        hasManyAppActions && !isExpanded && !normalizedScopeSearch
                          ? [...appWildcardScopes, ...appActionScopes.slice(0, APP_SCOPE_PREVIEW_LIMIT)]
                          : group.scopes;

                      return (
                        <div
                          key={group.id}
                          data-oauth-scope-group={group.id}
                          className="space-y-2 rounded-md border p-3"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {group.title}
                            </div>
                            {appActionScopes.length > 0 && (
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {appActionScopes.length} {appActionScopes.length === 1 ? "action" : "actions"}
                              </span>
                            )}
                          </div>
                          {visibleScopes.map((scope) => {
                            const scopeDisplayLabel = getScopeDisplayLabel(scope, scopeLabels);
                            const scopeDefinition = getOAuthScopeDefinition(scope);
                            const installedAppScope = parseInstalledAppScope(scope);
                            const wildcardScope = installedAppScope
                              ? buildInstalledAppScope({
                                  installedAppId: installedAppScope.installedAppId,
                                  actionId: "*",
                                })
                              : null;
                            const checked =
                              formState.scopes.includes(scope) ||
                              (installedAppScope?.actionId !== "*" &&
                                wildcardScope !== null &&
                                formState.scopes.includes(wildcardScope));
                            return (
                              <div key={scope} className="flex items-start gap-2">
                                <Checkbox
                                  aria-label={`Toggle ${scopeDisplayLabel}`}
                                  checked={checked}
                                  onCheckedChange={(checked) => toggleScope(scope, checked === true)}
                                />
                                <div className="min-w-0 space-y-1">
                                  <div className="break-words text-sm" title={scope}>
                                    {scopeDisplayLabel}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {scopeLabels[scope] ??
                                      (scopeDefinition
                                        ? `Mapped permissions: ${scopeDefinition.permissionActions.join(", ")}`
                                        : "This scope is no longer available. Remove it before saving.")}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {hasManyAppActions && !normalizedScopeSearch && (
                            <div className="flex items-center justify-between border-t pt-2 max-md:flex-wrap max-md:gap-2">
                              <span className="text-xs text-muted-foreground">
                                Showing {isExpanded ? appActionScopes.length : APP_SCOPE_PREVIEW_LIMIT} of{" "}
                                {appActionScopes.length} actions
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-expanded={isExpanded}
                                onClick={() => {
                                  setExpandedScopeGroups((previous) => {
                                    const next = new Set(previous);
                                    if (isExpanded) {
                                      next.delete(group.id);
                                    } else {
                                      next.add(group.id);
                                    }
                                    return next;
                                  });
                                }}
                                className="h-7 px-2 text-xs"
                              >
                                {isExpanded
                                  ? `Show first ${APP_SCOPE_PREVIEW_LIMIT}`
                                  : `Show all ${appActionScopes.length}`}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-sm text-muted-foreground">No matching scopes</div>
                  )
                ) : (
                  <div className="text-sm text-muted-foreground">No scopes available</div>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="redirect-uri">Redirect URIs</Label>
              <p className="text-xs text-muted-foreground max-md:break-all">
                Example for ChatGPT GPTs: https://chat.openai.com/aip/{`{g-YOUR-GPT-ID-HERE}`}/oauth/callback
              </p>
              <div className="mt-2 flex gap-2">
                <Input
                  id="redirect-uri"
                  value={redirectUriDraft}
                  onChange={(e) => setRedirectUriDraft(e.target.value)}
                  placeholder="https://example.com/oauth/callback"
                />
                <Button variant="outline" type="button" onClick={addRedirectUri}>
                  Add
                </Button>
              </div>
              {formState.redirect_uris.length ? (
                <div className="mt-3 space-y-2">
                  {formState.redirect_uris.map((uri) => (
                    <div
                      key={uri}
                      className="flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm"
                    >
                      <span className="min-w-0 flex-1 break-all">{uri}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={() => removeRedirectUri(uri)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">No redirect URIs configured.</p>
              )}
            </div>
            <div className="flex items-center gap-2 max-md:flex-wrap">
              <Checkbox
                checked={formState.is_active}
                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, is_active: checked === true }))}
              />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter className="max-md:gap-2">
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createClient.isPending || updateClient.isPending}>
              {editingClient ? "Save changes" : "Create credential"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4 max-md:[overflow-wrap:anywhere]">
          <DialogHeader className="max-md:pr-6">
            <DialogTitle>Delete API Credential</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="max-md:gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedClient && deleteClient.mutate({ id: selectedClient.id })}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
        <DialogContent className="max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4 max-md:[overflow-wrap:anywhere]">
          <DialogHeader className="max-md:pr-6">
            <DialogTitle>Regenerate Client Secret</DialogTitle>
            <DialogDescription>The old secret will stop working immediately.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="max-md:gap-2">
            <Button variant="outline" onClick={() => setRegenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => selectedClient && regenerateSecret.mutate({ id: selectedClient.id })}
              disabled={regenerateSecret.isPending}
            >
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={secretDialogOpen} onOpenChange={setSecretDialogOpen}>
        <DialogContent className="max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4 max-md:[overflow-wrap:anywhere]">
          <DialogHeader className="max-md:pr-6">
            <DialogTitle>Client Secret</DialogTitle>
            <DialogDescription>Copy this secret now. You will not be able to see it again.</DialogDescription>
          </DialogHeader>
          {generatedSecret && (
            <div className="space-y-3">
              <div>
                <Label>Client ID</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={generatedSecret.client_id} readOnly />
                  <Button size="sm" variant="outline" onClick={() => copyValue(generatedSecret.client_id, "Client ID")}>
                    Copy
                  </Button>
                </div>
              </div>
              <div>
                <Label>Client Secret</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={generatedSecret.client_secret} readOnly />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyValue(generatedSecret.client_secret, "Client secret")}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="max-md:gap-2">
            <Button onClick={() => setSecretDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
